import Component from '@glimmer/component';
import formatDateString from '../../../helpers/format-date-string.ts';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import type EmployeeModel from 'tio-data/models/employee';
import type GenericRelationshipModel from 'tio-data/models/generic-relationship';
import type Owner from '@ember/owner';
import type Store from '@ember-data/store';

interface ProfileRoleDelegationCoveringRowSignature {
  Args: {
    coveringRole: GenericRelationshipModel;
    cellClass: string;
    employeeRoute?: string;
    usePersonRoute?: boolean;
  };
  Element: HTMLElement;
}

export default class ProfileRoleDelegationCoveringRowComponent extends Component<ProfileRoleDelegationCoveringRowSignature> {
  @service declare store: Store;

  @tracked coveredEmployee: EmployeeModel | null = null;

  loadCoveredEmployee = task(async () => {
    const coveredEmployeeId = this.args.coveringRole.delegateOf?.governorId;
    if (!coveredEmployeeId) {
      return;
    }
    this.coveredEmployee = await this.store.findRecord<EmployeeModel>(
      'employee',
      coveredEmployeeId,
      {
        include: 'person',
      }
    );
  });

  constructor(owner: Owner, args: ProfileRoleDelegationCoveringRowSignature['Args']) {
    super(owner, args);
    this.loadCoveredEmployee.perform();
  }

  get linkModelId() {
    if (this.args.usePersonRoute) {
      return this.coveredEmployee?.person?.id;
    }
    return this.coveredEmployee?.id;
  }

  get expirationDate() {
    return this.args.coveringRole.expiresAfter ?? null;
  }

  <template>
    {{#if this.loadCoveredEmployee.isRunning}}
      <tr>
        <td class={{@cellClass}} colspan="100">
          <div class="h-4 bg-gray-200 rounded-full"></div>
        </td>
      </tr>
    {{else}}
      <tr>
        <td class={{@cellClass}}>
          {{#if this.coveredEmployee}}
            {{#if @employeeRoute}}
              {{#if this.linkModelId}}
                <LinkTo
                  @route={{@employeeRoute}}
                  @model={{this.linkModelId}}
                >{{this.coveredEmployee.fullName}}</LinkTo>
              {{else}}
                {{this.coveredEmployee.fullName}}
              {{/if}}
            {{else}}
              {{this.coveredEmployee.fullName}}
            {{/if}}
          {{else}}
            <span class="tio-fg-disabled">{{t "common.not_applicable"}}</span>
          {{/if}}
        </td>
        <td class={{@cellClass}}>{{@coveringRole.relationshipType}}</td>
        <td class={{@cellClass}}>{{safeFormatDate
            @coveringRole.createdAt
            month="2-digit"
            day="2-digit"
            year="numeric"
          }}</td>
        <td class={{@cellClass}}>{{if
            @coveringRole.isActive
            (t "common.active")
            (t "common.deactivated")
          }}</td>
        <td class={{@cellClass}}>{{if
            this.expirationDate
            (formatDateString this.expirationDate)
            (t "common.not_applicable")
          }}</td>
      </tr>
    {{/if}}
  </template>
}
